import classNames from 'classnames'
import type { ElementFormatType } from 'lexical'
import { Button } from '@winnin/winnin_ui.components.button'
import { Icon } from '@winnin/winnin_ui.components.icon'
import { ThemeProvider } from '@winnin/winnin_ui.theme.theme-provider'

import { Highlight, Lettering } from 'assets/icons'
import ColorPickerWithActions from 'components/ColorPickerWithActions'
import DropdownWithLabel from 'components/DropdownWithLabel'
import { useTranslate } from 'lib/tolgee'
import { TOLGEE_GLOBALS } from 'utils'

import { ALIGN_OPTIONS, AVAILABLE_HIGHLIGHT_COLORS } from './text-toolbar.consts'
import translations from './text-toolbar.i18n'
import type { ITextToolbarView } from './text-toolbar.types'

import './text-toolbar.styles.scss'

export const TextToolbarView = ({
  handleColorChange,
  handleAlignment,
  textColor,
  isBold,
  isItalic,
  isUnderline,
  alignment,
  handleUndo,
  handleRedo,
  handleFormat,
  openLinkModal,
  isLinkEditMode,
  linkValue,
  onLinkChange,
  submitLink,
  fontFamilyOptions,
  fontSizeOptions,
  handleHighlightChange,
  highlightColor,
  isLink,
  isColorModalOpen,
  isHighlightModalOpen,
  handleColorModal,
  handleHighlightModal,
  handleCloseLinkModal,
  align = 'left',
}: ITextToolbarView) => {
  const t = useTranslate()

  return (
    <ThemeProvider>
      <div className={classNames(['toolbar', { alignLeft: align === 'left', alignRight: align === 'right' }])}>
        <button onClick={handleUndo} title={t(translations.undo)} data-testid="textComponent">
          <Icon icon="backArrow" height="14px" width="14px" fill="var(--color_neutral_800)" />
        </button>
        <button onClick={handleRedo} className="redoButton" title={t(translations.redo)} data-testid="textComponent">
          <Icon icon="backArrow" height="14px" width="14px" fill="var(--color_neutral_800)" />
        </button>

        <div className="fontFamilyWrapper">
          <DropdownWithLabel label={''} options={fontFamilyOptions} />
        </div>

        <div className="fontSizeWrapper">
          <DropdownWithLabel label={''} options={fontSizeOptions} />
        </div>

        <button
          onClick={() => handleFormat('bold')}
          className={`boldButton toolbar-button ${isBold ? 'active' : ''}`}
          title={t(translations.bold)}
          data-testid="textComponent"
        >
          B
        </button>
        <button
          onClick={() => handleFormat('italic')}
          className={`italicButton toolbar-button ${isItalic ? 'active' : ''}`}
          title={t(translations.italic)}
          data-testid="textComponent"
        >
          I
        </button>
        <button
          onClick={() => handleFormat('underline')}
          className={`underlineButton toolbar-button ${isUnderline ? 'active' : ''}`}
          title={t(translations.underscore)}
          style={{ textDecoration: 'none' }}
          data-testid="textComponent"
        >
          U
        </button>

        <div className="linkButtonWrapper">
          <button
            onClick={openLinkModal}
            className={`toolbar-button ${isLink ? 'active' : ''}`}
            aria-label={t(translations.insertLink)}
            title={t(translations.insertLink)}
            type="button"
            data-testid="textComponent"
          >
            <Icon icon="chain" fill="var(--color_neutral_800)" />
          </button>
          {isLinkEditMode && (
            <div className="linkEditContent">
              <input
                type="text"
                value={linkValue}
                onChange={onLinkChange}
                data-testid="textComponent"
                placeholder="https://insights.winnin.com/"
              />
              <div className="buttonsRow">
                <Button appearance="link" onClick={handleCloseLinkModal}>
                  {t(TOLGEE_GLOBALS.cancel)}
                </Button>
                <Button type="button" onClick={submitLink} data-testid="textComponent">
                  {t(TOLGEE_GLOBALS.apply)}
                </Button>
              </div>
            </div>
          )}
        </div>

        {ALIGN_OPTIONS.map(option => (
          <button
            key={option.value}
            onClick={() => handleAlignment(option.value as ElementFormatType)}
            className={`toolbar-button ${alignment === option.value ? 'active' : ''}`}
            title={t(option.title)}
            data-testid="textComponent"
          >
            {option.icon({ style: { width: 16, height: 16, fill: 'var(--color_neutral_800)' } })}
          </button>
        ))}

        <div className="colorPickerWrapper">
          <button
            className="toolbar-button colorButton"
            onClick={() => handleColorModal(true)}
            title={t(translations.textColor)}
          >
            <Lettering height="12px" width="12px" fill="var(--color_neutral_800)" />
            <div className="colorBar" style={{ backgroundColor: textColor }} />
          </button>
          <ColorPickerWithActions
            currentColor={textColor}
            onSelect={handleColorChange}
            closePicker={() => handleColorModal(false)}
            isOpen={isColorModalOpen}
          />
        </div>

        <div className="colorPickerWrapper">
          <button
            className="toolbar-button colorButton"
            title={t(translations.highlightColor)}
            onClick={() => handleHighlightModal(true)}
          >
            <Highlight height="12px" width="12px" fill="var(--color_neutral_800)" />
            <div className="colorBar" style={{ backgroundColor: highlightColor }} />
          </button>
          <ColorPickerWithActions
            currentColor={highlightColor}
            onSelect={handleHighlightChange}
            closePicker={() => handleHighlightModal(false)}
            isOpen={isHighlightModalOpen}
            availableColors={AVAILABLE_HIGHLIGHT_COLORS}
          />
        </div>
      </div>
    </ThemeProvider>
  )
}
